import React, { useEffect, useState, createElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Copy, Check, AlertTriangle, ExternalLink, Info } from 'lucide-react';
import { formConfigsApi, signupFormsApi, emailTemplatesApi, SignupFormRecord, EmailTemplateRecord, FormConfigRecord } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
// --- Helpers ---
function extractLookupId(val: any): string {
  if (!val) return '';
  if (typeof val === 'object' && val.ID) return String(val.ID);
  if (typeof val === 'number') return String(val);
  if (typeof val === 'string') return val;
  return '';
}
function extractFormFields(html: string): string[] {
  const fields: string[] = [];
  const regex = /(?:name|id)=["']([^"']+)["']/gi;
  let match;
  while ((match = regex.exec(html)) !== null) {
    const name = match[1];
    if (name && !fields.includes(name) && !['submit', 'button'].includes(name.toLowerCase())) {
      fields.push(name);
    }
  }
  return fields;
}
function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '-9999px';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  return new Promise((resolve, reject) => {
    document.execCommand('copy') ? resolve() : reject();
    textarea.remove();
  });
}
export function FormConfiguration() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const isEditing = !!id && id !== 'new';
  // Derive team from workspace scope — strip "Team" suffix
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  // Data Lists
  const [forms, setForms] = useState<SignupFormRecord[]>([]);
  const [templates, setTemplates] = useState<EmailTemplateRecord[]>([]);
  // Selection State
  const [selectedFormId, setSelectedFormId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  // Content for Inspector
  const [selectedFormHtml, setSelectedFormHtml] = useState('');
  const [selectedTemplateHtml, setSelectedTemplateHtml] = useState('');
  // UI State
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [originalFormId, setOriginalFormId] = useState(''); // To detect changes in edit mode
  // Initial Data Load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [formsData, templatesData] = await Promise.all([signupFormsApi.list(), emailTemplatesApi.list()]);
        // Filter forms and templates by team
        const teamFilteredForms = formsData.filter(f => f.Team === currentWorkspace.name || f.Team === team);
        const teamFilteredTemplates = templatesData.filter(t => t.Team === currentWorkspace.name || t.Team === team);
        setForms(teamFilteredForms);
        setTemplates(teamFilteredTemplates);
        // If editing, fetch the config record
        if (isEditing && id) {
          const config = await formConfigsApi.get(id);
          if (config) {
            const formId = extractLookupId(config.Sign_Up_Form);
            const templateId = extractLookupId(config.Email_Template);
            setSelectedFormId(formId);
            setOriginalFormId(formId);
            setSelectedTemplateId(templateId);
          } else {
            toast.error('Configuration not found');
            navigate('/configurations');
          }
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        toast.error('Failed to load configuration data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, isEditing, navigate]);
  // Fetch Form HTML when selection changes
  useEffect(() => {
    if (!selectedFormId) {
      setSelectedFormHtml('');
      return;
    }
    // Check if we already have the full record in the list (optimization)
    const formInList = forms.find(f => f.ID === selectedFormId);
    if (formInList && formInList.HTML) {
      setSelectedFormHtml(formInList.HTML);
    } else {
      // Fetch full record if needed
      signupFormsApi.get(selectedFormId).then(record => {
        if (record) setSelectedFormHtml(record.HTML || '');
      }).catch(err => console.error('Failed to fetch form HTML', err));
    }
  }, [selectedFormId, forms]);
  // Fetch Template HTML when selection changes
  useEffect(() => {
    if (!selectedTemplateId) {
      setSelectedTemplateHtml('');
      return;
    }
    // Check if we already have the full record in the list
    const templateInList = templates.find(t => t.ID === selectedTemplateId);
    if (templateInList && templateInList.Email_HTML_Body) {
      setSelectedTemplateHtml(templateInList.Email_HTML_Body);
    } else {
      // Fetch full record if needed
      emailTemplatesApi.get(selectedTemplateId).then(record => {
        if (record) setSelectedTemplateHtml(record.Email_HTML_Body || '');
      }).catch(err => console.error('Failed to fetch template HTML', err));
    }
  }, [selectedTemplateId, templates]);
  const handleSave = async () => {
    if (!selectedFormId || !selectedTemplateId) {
      toast.error('Please select both a form and a template');
      return;
    }
    setIsSaving(true);
    try {
      // Check if this form already has a configuration with a DIFFERENT template
      const existingConfigs = await formConfigsApi.findByFormId(selectedFormId);
      const conflicting = existingConfigs.filter(c => {
        // Skip the current config if editing
        if (isEditing && id && c.ID === id) return false;
        const linkedTemplateId = extractLookupId(c.Email_Template);
        return linkedTemplateId !== selectedTemplateId;
      });
      if (conflicting.length > 0) {
        // Find the name of the already-linked template for a helpful message
        let linkedTemplateName = 'another template';
        const linkedId = extractLookupId(conflicting[0].Email_Template);
        const linkedTemplate = templates.find(t => t.ID === linkedId);
        if (linkedTemplate) {
          linkedTemplateName = `"${linkedTemplate.Email_Subject}"`;
        }
        toast.error('This form is already linked to ' + linkedTemplateName, {
          description: 'A form can only be linked to one email template. Remove the existing configuration first, or select a different form.',
          duration: 8000
        });
        setIsSaving(false);
        return;
      }
      const payload = {
        Sign_Up_Form: selectedFormId,
        Email_Template: selectedTemplateId,
        Team: team
      };
      if (isEditing && id) {
        await formConfigsApi.update(id, payload);
        toast.success('Configuration updated successfully');
      } else {
        await formConfigsApi.create(payload);
        toast.success('Configuration created successfully');
      }
      navigate('/configurations');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to save configuration', {
        description: msg
      });
    } finally {
      setIsSaving(false);
    }
  };
  const handleCopyVariable = (name: string) => {
    const variable = `{{${name}}}`;
    copyToClipboard(variable).then(() => toast.success(`Copied ${variable}`)).catch(() => toast.error('Failed to copy'));
  };
  // --- Render Helpers ---
  const formFields = selectedFormHtml ? extractFormFields(selectedFormHtml) : [];
  const hasInspector = selectedFormId && selectedTemplateId;
  const isVariableInTemplate = (varName: string) => {
    if (!selectedTemplateHtml) return false;
    return selectedTemplateHtml.includes(`{{${varName}}}`);
  };
  if (loading) {
    return <div className="flex items-center justify-center py-24" data-id="element-2841">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-2842" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-2843">
          Loading configuration...
        </span>
      </div>;
  }
  return <div className="space-y-6 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2844">
      {/* Header */}
      <div className="flex items-center gap-4" data-id="element-2845">
        <Link to="/configurations" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-2846">
          <ArrowLeft className="h-5 w-5" data-id="element-2847" />
        </Link>
        <div data-id="element-2848">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2849">
            {isEditing ? 'Edit Configuration' : 'New Configuration'}
          </h1>
          <p className="text-sm text-gray-500" data-id="element-2850">
            Link a signup form to an email template and verify variables.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start" data-id="element-2851">
        {/* Left Column: Form Fields */}
        <div className="lg:col-span-1 space-y-6" data-id="element-2852">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-6" data-id="element-2853">
            {/* Form Selection */}
            <div className="space-y-2" data-id="element-2854">
              <label className="block text-sm font-medium text-gray-700" data-id="element-2855">
                Sign Up Form
              </label>
              <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={selectedFormId} onChange={e => setSelectedFormId(e.target.value)} data-id="element-2856">
                <option value="" data-id="element-2857">Select a form...</option>
                {forms.map(form => <option key={form.ID} value={form.ID} data-id="element-2858">
                    {form.Form_Name}
                  </option>)}
              </select>
              <p className="text-xs text-gray-500" data-id="element-2859">
                The form that triggers this configuration.
              </p>
            </div>

            {/* Change Warning */}
            {isEditing && selectedFormId !== originalFormId && <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex gap-2" data-id="element-2860">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-2861" />
                <div className="text-xs text-amber-800" data-id="element-2862">
                  <span className="font-medium" data-id="element-2863">Form changed.</span> Check the
                  variable inspector to ensure the template supports the new
                  form's fields.
                </div>
              </div>}

            {/* Template Selection */}
            <div className="space-y-2" data-id="element-2864">
              <label className="block text-sm font-medium text-gray-700" data-id="element-2865">
                Email Template
              </label>
              <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)} data-id="element-2866">
                <option value="" data-id="element-2867">Select a template...</option>
                {templates.map(template => <option key={template.ID} value={template.ID} data-id="element-2868">
                    {template.Email_Subject}
                  </option>)}
              </select>
              <p className="text-xs text-gray-500" data-id="element-2869">
                The email sent when the form is submitted.
              </p>
            </div>

            {/* Team Field */}
            <div className="space-y-2" data-id="element-2870">
              <label className="block text-sm font-medium text-gray-700" data-id="element-2871">
                Team
              </label>
              <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-2872" />
              <p className="text-xs text-gray-400" data-id="element-2873">
                Automatically set from your current workspace scope
              </p>
            </div>

            {/* Save Button */}
            <div className="pt-4" data-id="element-2874">
              <button onClick={handleSave} disabled={isSaving || !selectedFormId || !selectedTemplateId} className="w-full inline-flex justify-center items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-2875">
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-2876" /> : <Save className="h-4 w-4" data-id="element-2877" />}
                {isSaving ? 'Saving...' : isEditing ? 'Update Configuration' : 'Create Configuration'}
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Variable Inspector */}
        <div className="lg:col-span-2" data-id="element-2878">
          {hasInspector ? <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-2879">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-2880">
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-2881">
                  <Info className="h-4 w-4 text-blue-500" data-id="element-2882" />
                  Variable Inspector
                </h3>
                {selectedTemplateId && <Link to={`/templates/${selectedTemplateId}`} target="_blank" className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1" data-id="element-2883">
                    Edit Template <ExternalLink className="h-3 w-3" data-id="element-2884" />
                  </Link>}
              </div>

              <div className="p-6 space-y-8" data-id="element-2885">
                {/* System Variables */}
                <div className="space-y-3" data-id="element-2886">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2887">
                    System Variables
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2888">
                    {['confirmation_link', 'team_name'].map(sysVar => {
                  const found = isVariableInTemplate(sysVar);
                  return <button key={sysVar} onClick={() => handleCopyVariable(sysVar)} className={`flex items-center justify-between p-3 rounded-md border text-left transition-all ${found ? 'bg-green-50 border-green-200 hover:border-green-300' : 'bg-amber-50 border-amber-200 hover:border-amber-300'}`} data-id="element-2889">
                          <div className="flex items-center gap-2" data-id="element-2890">
                            <code className={`text-sm font-medium ${found ? 'text-green-700' : 'text-amber-700'}`} data-id="element-2891">
                              {`{{${sysVar}}}`}
                            </code>
                          </div>
                          {found ? <div className="flex items-center gap-1 text-xs text-green-600 font-medium" data-id="element-2892">
                              <Check className="h-3 w-3" data-id="element-2893" /> Found
                            </div> : <div className="flex items-center gap-1 text-xs text-amber-600 font-medium" data-id="element-2894">
                              <AlertTriangle className="h-3 w-3" data-id="element-2895" /> Missing
                            </div>}
                        </button>;
                })}
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-2896">
                    <span className="font-medium" data-id="element-2897">Note:</span>{' '}
                    <code data-id="element-2898">{`{{confirmation_link}}`}</code> is required for
                    double opt-in.
                  </p>
                </div>

                {/* Form Variables */}
                <div className="space-y-3" data-id="element-2899">
                  <div className="flex items-center justify-between" data-id="element-2900">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2901">
                      Form Variables
                    </h4>
                    <span className="text-xs text-gray-400" data-id="element-2902">
                      {formFields.length} fields found in form
                    </span>
                  </div>

                  {formFields.length === 0 ? <div className="text-sm text-gray-500 italic bg-gray-50 p-4 rounded border border-dashed border-gray-200" data-id="element-2903">
                      No input fields found in the selected form's HTML.
                    </div> : <div className="flex flex-wrap gap-2" data-id="element-2904">
                      {formFields.map(field => {
                  const found = isVariableInTemplate(field);
                  return <button key={field} onClick={() => handleCopyVariable(field)} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors ${found ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`} title={found ? 'Found in template' : 'Not used in template (click to copy)'} data-id="element-2905">
                            <code data-id="element-2906">{field}</code>
                            {found && <Check className="h-3 w-3" data-id="element-2907" />}
                          </button>;
                })}
                    </div>}
                  <p className="text-xs text-gray-500" data-id="element-2908">
                    Green variables are currently used in the selected template.
                    Gray ones are available but unused.
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-2909">
                <p className="text-xs text-gray-500 flex items-start gap-2" data-id="element-2910">
                  <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-2911" />
                  If you need to add variables to the template, click "Edit
                  Template" above. The variables will work automatically once
                  added to the HTML body.
                </p>
              </div>
            </div> : <div className="h-full min-h-[300px] flex flex-col items-center justify-center text-center p-8 bg-gray-50 border border-gray-200 border-dashed rounded-lg" data-id="element-2912">
              <div className="bg-white p-3 rounded-full shadow-sm mb-4" data-id="element-2913">
                <Info className="h-6 w-6 text-gray-400" data-id="element-2914" />
              </div>
              <h3 className="text-sm font-medium text-gray-900" data-id="element-2915">
                Variable Inspector
              </h3>
              <p className="text-sm text-gray-500 mt-1 max-w-xs" data-id="element-2916">
                Select both a form and a template to see which variables are
                being used and which are missing.
              </p>
            </div>}
        </div>
      </div>
    </div>;
}